import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowRight, Loader2, PackageSearch, Star, RotateCcw, Sparkles, BadgeCheck, Zap, Scale,
  IndianRupee, Truck, ShieldCheck, MapPin, Check,
} from "lucide-react";
import api, { inr, fmtErr } from "../../lib/api";
import { toast } from "sonner";
import { SectionHead, fadeUp } from "../marketing/Blocks";

const inputCls = "mono w-full rounded-xl border border-white/15 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:border-shipany-cyan focus:outline-none";
const labelCls = "mb-1.5 block text-[11px] font-bold uppercase tracking-widest text-slate-400";

const COMPARE_PARAMS = [
  { icon: IndianRupee, t: "Prices", d: "Courier charges differ by lane, weight and service type." },
  { icon: Truck, t: "Delivery timelines", d: "Estimated delivery varies across couriers on the same lane." },
  { icon: MapPin, t: "Serviceability", d: "Not every courier serves every pincode — checked live." },
  { icon: BadgeCheck, t: "COD capabilities", d: "COD support and charges differ per courier." },
  { icon: ShieldCheck, t: "Coverage", d: "Network depth varies across zones and remote pincodes." },
  { icon: Star, t: "Performance", d: "Ratings reflect real delivery behaviour on the platform." },
];

const CHOICES = [
  { icon: IndianRupee, t: "Lowest Cost", d: "Choose the most economical option.", accent: "text-emerald-400", bg: "bg-emerald-500/15" },
  { icon: Zap, t: "Fastest Delivery", d: "Prioritize delivery speed.", accent: "text-[#FF9933]", bg: "bg-[#FF9933]/15" },
  { icon: Scale, t: "Best Overall", d: "Balance cost and delivery performance.", accent: "text-shipany-cyan", bg: "bg-shipany-cyan/15" },
];

export function CompareInfo() {
  return (
    <>
      <section className="bg-[#071B3A] py-20 sm:py-24" data-testid="compare-params-section">
        <div className="mx-auto max-w-7xl px-5">
          <SectionHead kicker="Why compare" title="Compare Before You Ship"
            sub="Different couriers may offer different prices, delivery timelines, serviceability, COD capabilities, coverage and performance. ShipAny brings these options together so you can make better shipping decisions." />
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {COMPARE_PARAMS.map((p, i) => (
              <motion.div key={p.t} {...fadeUp} transition={{ duration: 0.5, delay: (i % 3) * 0.08 }}
                data-testid={`compare-param-${p.t.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="flex gap-4 rounded-2xl border border-white/10 bg-white/[0.04] p-6 transition-colors hover:border-shipany-cyan/40">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-shipany-blue/30">
                  <p.icon className="h-5 w-5 text-shipany-cyan" />
                </span>
                <div>
                  <h3 className="font-display text-sm font-bold text-white">{p.t}</h3>
                  <p className="mt-1 text-xs leading-relaxed text-slate-400">{p.d}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-shipany-navy py-20 sm:py-24" data-testid="compare-choices-section">
        <div className="mx-auto max-w-6xl px-5">
          <SectionHead kicker="Your call" title="One Shipment. Multiple Choices." />
          <div className="mt-12 grid gap-5 sm:grid-cols-3">
            {CHOICES.map((c, i) => (
              <motion.div key={c.t} {...fadeUp} transition={{ duration: 0.5, delay: i * 0.1 }}
                data-testid={`compare-choice-${c.t.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="rounded-2xl border border-white/10 bg-white/[0.04] p-7 text-center transition-all duration-300 hover:-translate-y-1.5 hover:border-shipany-cyan/40">
                <span className={`mx-auto flex h-12 w-12 items-center justify-center rounded-2xl ${c.bg}`}>
                  <c.icon className={`h-5 w-5 ${c.accent}`} />
                </span>
                <h3 className="font-display mt-4 text-lg font-bold text-white">{c.t}</h3>
                <p className="mt-1.5 text-sm text-slate-400">{c.d}</p>
              </motion.div>
            ))}
          </div>
          <motion.div {...fadeUp} className="mt-12 text-center">
            <p className="font-display text-xl font-bold text-white" data-testid="compare-cta-title">Compare Your Shipping Rates</p>
            <a href="#rate-calculator" data-testid="compare-calc-now-btn"
              className="mt-5 inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-[#FF9933] to-[#FF8000] px-8 py-3.5 font-display text-sm font-bold text-[#071B3A] transition-all hover:shadow-[0_8px_30px_rgba(255,153,51,0.45)]">
              Calculate Now <ArrowRight className="h-4 w-4" />
            </a>
          </motion.div>
        </div>
      </section>
    </>
  );
}

export default function CompareCalculator() {
  const [form, setForm] = useState({
    pickup_pincode: "", delivery_pincode: "", package_type: "box",
    weight: "1", length: "10", breadth: "10", height: "10",
    payment_mode: "Prepaid", cod_amount: "",
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const compare = async (e) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(form.pickup_pincode) || !/^\d{6}$/.test(form.delivery_pincode)) {
      toast.error("Enter valid 6-digit pickup and delivery pincodes");
      return;
    }
    if (!(parseFloat(form.weight) > 0)) {
      toast.error("Enter a valid shipment weight");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/rates/calculate", {
        pickup_pincode: form.pickup_pincode,
        delivery_pincode: form.delivery_pincode,
        weight: parseFloat(form.weight),
        length: parseFloat(form.length) || 10,
        breadth: parseFloat(form.breadth) || 10,
        height: parseFloat(form.height) || 10,
        payment_mode: form.payment_mode,
        cod_amount: parseFloat(form.cod_amount) || 0,
        package_type: form.package_type,
      });
      setResult(data);
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setLoading(false);
    }
  };

  const quotes = result?.quotes || [];
  const recommended = quotes.find((q) => q.recommended) || quotes[0];
  const reasons = recommended ? [
    ...(recommended.badges || []).map((b) => b === "Lowest Rate" ? "Lowest available rate" : b === "Fastest" ? "Faster delivery" : b === "Top Rated" ? "Strong serviceability" : b),
    "Better shipment suitability",
  ] : [];

  return (
    <div data-testid="compare-calc-card"
      className="relative overflow-hidden rounded-3xl border border-white/15 bg-white/[0.07] p-6 shadow-2xl shadow-black/40 backdrop-blur-2xl sm:p-8">
      <div className="pointer-events-none absolute -right-20 -top-20 h-56 w-56 rounded-full bg-shipany-cyan/15 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-16 -left-16 h-48 w-48 rounded-full bg-[#FF9933]/10 blur-3xl" />

      <AnimatePresence mode="wait">
        {!result ? (
          <motion.form key="form" onSubmit={compare} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.3 }} className="relative">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-[#FF9933] to-[#10B981]">
                <PackageSearch className="h-5 w-5 text-white" />
              </span>
              <div>
                <h3 className="font-display text-lg font-bold text-white">Find the Best Courier for Every Shipment</h3>
                <p className="text-xs text-slate-400">Live rates from every enabled courier partner — compared instantly.</p>
              </div>
            </div>
            <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <div><label className={labelCls}>From Pincode</label>
                <input data-testid="compare-from-pincode-input" value={form.pickup_pincode} maxLength={6} inputMode="numeric" placeholder="110001"
                  onChange={(e) => setForm({ ...form, pickup_pincode: e.target.value.replace(/\D/g, "") })} className={inputCls} /></div>
              <div><label className={labelCls}>To Pincode</label>
                <input data-testid="compare-to-pincode-input" value={form.delivery_pincode} maxLength={6} inputMode="numeric" placeholder="400001"
                  onChange={(e) => setForm({ ...form, delivery_pincode: e.target.value.replace(/\D/g, "") })} className={inputCls} /></div>
              <div><label className={labelCls}>Shipment Type</label>
                <select data-testid="compare-package-type" value={form.package_type} onChange={set("package_type")}
                  className="w-full rounded-xl border border-white/15 bg-white/5 px-4 py-3 text-sm text-white focus:border-shipany-cyan focus:outline-none">
                  <option value="box" className="bg-[#071B3A]">Box / Parcel</option>
                  <option value="document" className="bg-[#071B3A]">Document</option>
                </select></div>
              <div><label className={labelCls}>Actual Weight (kg)</label>
                <input data-testid="compare-weight-input" value={form.weight} inputMode="decimal" onChange={set("weight")} className={inputCls} placeholder="1.0" /></div>
              <div><label className={labelCls}>Length (cm)</label>
                <input data-testid="compare-length-input" value={form.length} inputMode="decimal" onChange={set("length")} className={inputCls} placeholder="10" /></div>
              <div><label className={labelCls}>Width (cm)</label>
                <input data-testid="compare-width-input" value={form.breadth} inputMode="decimal" onChange={set("breadth")} className={inputCls} placeholder="10" /></div>
              <div><label className={labelCls}>Height (cm)</label>
                <input data-testid="compare-height-input" value={form.height} inputMode="decimal" onChange={set("height")} className={inputCls} placeholder="10" /></div>
              <div><label className={labelCls}>Payment Mode</label>
                <select data-testid="compare-payment-mode" value={form.payment_mode} onChange={set("payment_mode")}
                  className="w-full rounded-xl border border-white/15 bg-white/5 px-4 py-3 text-sm text-white focus:border-shipany-cyan focus:outline-none">
                  <option className="bg-[#071B3A]">Prepaid</option>
                  <option className="bg-[#071B3A]">COD</option>
                </select></div>
              {form.payment_mode === "COD" && (
                <div className="sm:col-span-2"><label className={labelCls}>COD Amount (₹)</label>
                  <input data-testid="compare-cod-amount-input" value={form.cod_amount} inputMode="decimal" placeholder="Amount to collect"
                    onChange={(e) => setForm({ ...form, cod_amount: e.target.value.replace(/[^\d.]/g, "") })} className={inputCls} /></div>
              )}
            </div>
            <button data-testid="compare-rates-btn" disabled={loading}
              className="mt-6 flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#FF9933] to-[#FF8000] py-3.5 font-display text-sm font-bold text-[#071B3A] transition-all hover:shadow-[0_8px_30px_rgba(255,153,51,0.4)] disabled:opacity-60">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null} COMPARE RATES <ArrowRight className="h-4 w-4" />
            </button>
          </motion.form>
        ) : (
          <motion.div key="results" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.3 }} className="relative" data-testid="compare-results">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h3 className="font-display text-lg font-bold text-white">{quotes.length} courier option{quotes.length === 1 ? "" : "s"} found</h3>
                <p className="mono text-xs text-shipany-cyan">{form.pickup_pincode} → {form.delivery_pincode} · chargeable {result.chargeable_weight} kg · {form.payment_mode}</p>
              </div>
              <button onClick={() => setResult(null)} data-testid="compare-reset-btn"
                className="flex items-center gap-1.5 rounded-lg border border-white/15 px-3 py-1.5 text-xs font-semibold text-slate-300 hover:bg-white/5">
                <RotateCcw className="h-3 w-3" /> New search
              </button>
            </div>

            {recommended && (
              <div className="mt-5 flex flex-col gap-4 rounded-2xl border border-[#10B981]/40 bg-[#10B981]/10 p-5 sm:flex-row sm:items-center" data-testid="compare-recommendation-card">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#10B981]/20">
                  <Sparkles className="h-5 w-5 text-[#10B981]" />
                </span>
                <div className="flex-1">
                  <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#10B981]">Recommended for this shipment</p>
                  <p className="mt-1 font-display text-base font-bold text-white" data-testid="compare-recommended-courier">
                    {recommended.courier_name} · {inr(recommended.final_price)} · {recommended.eta_days} day{recommended.eta_days > 1 ? "s" : ""}
                  </p>
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {reasons.map((r) => (
                      <span key={r} className="flex items-center gap-1 rounded-full bg-white/10 px-2.5 py-0.5 text-[10px] font-semibold text-slate-200">
                        <Check className="h-2.5 w-2.5 text-[#10B981]" />{r}
                      </span>
                    ))}
                  </div>
                </div>
                <Link to="/signup" data-testid="compare-recommended-ship-btn"
                  className="flex shrink-0 items-center justify-center gap-2 rounded-xl bg-[#10B981] px-6 py-3 font-display text-sm font-bold text-[#030B18] transition-all hover:shadow-[0_8px_30px_rgba(16,185,129,0.4)]">
                  SHIP NOW <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            )}

            <div className="mono mt-5 hidden grid-cols-12 gap-2 px-4 text-[10px] font-bold uppercase tracking-widest text-slate-500 lg:grid">
              <span className="col-span-3">Courier</span><span className="col-span-1">Est. Delivery</span>
              <span className="col-span-2">Shipping Rate</span><span className="col-span-2">COD Charges</span>
              <span className="col-span-1">Serviceability</span><span className="col-span-1">Rating</span><span className="col-span-2 text-right">Action</span>
            </div>
            <div className="mt-2 space-y-2">
              {quotes.map((q, i) => {
                const isRec = recommended && (q.courier_id || i) === (recommended.courier_id || quotes.indexOf(recommended));
                return (
                  <motion.div key={q.courier_id || i} initial={{ opacity: 0, x: -14 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: Math.min(i * 0.05, 0.4) }}
                    data-testid={`compare-result-row-${i + 1}`}
                    className={`grid grid-cols-2 items-center gap-2 rounded-xl border px-4 py-3 lg:grid-cols-12 ${isRec ? "border-[#10B981]/50 bg-[#10B981]/10" : "border-white/10 bg-white/5"}`}>
                    <div className="col-span-2 lg:col-span-3">
                      <p className="flex flex-wrap items-center gap-2 font-sans text-sm font-bold text-white">
                        {q.courier_name}
                        {isRec && <span className="rounded-full bg-[#10B981] px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-[#030B18]" data-testid="compare-best-value-badge">Best Value</span>}
                      </p>
                      {(q.badges || []).filter((b) => b).map((b) => (
                        <span key={b} className="mr-1.5 text-[9px] font-bold uppercase tracking-wider text-[#FF9933]">{b}</span>
                      ))}
                    </div>
                    <span className="text-xs text-slate-300 lg:col-span-1">{q.eta_days} day{q.eta_days > 1 ? "s" : ""}</span>
                    <span className="text-sm font-bold text-[#10B981] lg:col-span-2" data-testid={`compare-price-${i + 1}`}>{inr(q.final_price)}</span>
                    <span className="text-xs text-slate-300 lg:col-span-2">{form.payment_mode === "COD" ? (q.cod_charge ? inr(q.cod_charge) : "Included") : "—"}</span>
                    <span className="flex items-center gap-1 text-xs font-semibold text-emerald-400 lg:col-span-1"><Check className="h-3 w-3" />Yes</span>
                    <span className="flex items-center gap-1 text-xs text-amber-300 lg:col-span-1"><Star className="h-3 w-3 fill-amber-300" /> {q.rating ?? "4.2"}</span>
                    <span className="col-span-2 flex lg:col-span-2 lg:justify-end">
                      <Link to="/signup" data-testid={`compare-ship-now-${i + 1}`}
                        className={`rounded-lg px-4 py-2 text-xs font-bold transition-all ${isRec ? "bg-[#10B981] text-[#030B18] hover:shadow-[0_6px_20px_rgba(16,185,129,0.4)]" : "border border-white/20 text-white hover:bg-white/5"}`}>
                        SHIP NOW
                      </Link>
                    </span>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
